import SuperFetch from 'library/helpers/superFetch';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { globalAlertRulesActions } from './slice';

/**
 * Global Alert Rules saga (P4b). CRUD against the v5 /alerts/rules?type=global API — server
 * enforces org-wide-admin + the `globalAlertBuilder` flag; a non-admin/flag-off request 403s and
 * the saga surfaces that via the normal error toast (no client-side RBAC bypass to guard against).
 */
const selectOrgID = (state) => state.Auth.selectedProfile.orgID;
const okStatus = (res) => res && (res.statusCode === 200 || res.statusCode === 201);

function* fetchRules() {
	const orgID = yield select(selectOrgID);
	yield put(globalAlertRulesActions.loadRulesStart({ orgID }));
	try {
		const res = yield call(() => SuperFetch.get('/alerts/rules', {}, { type: 'global' }));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to load global alerts.');
		yield put(globalAlertRulesActions.loadRulesSuccess({ orgID, rules: res.data || [] }));
	} catch (error) {
		yield put(globalAlertRulesActions.loadRulesError({ orgID, error: error.message || String(error) }));
	}
}

// saveRule handles both create (no alertRuleID) and update (with alertRuleID); `rule.recipients`
// is a full-replace set on update (the API's semantics — see routesV5/alertRules).
function* saveRule(action) {
	const orgID = yield select(selectOrgID);
	const { alertRuleID, rule } = action.payload;
	try {
		const res = alertRuleID
			? yield call(() => SuperFetch.patch(`/alerts/rules/${alertRuleID}`, rule))
			: yield call(() => SuperFetch.post('/alerts/rules', { ...rule, type: 'global' }));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to save the global alert.');
		yield put(globalAlertRulesActions.saveRuleSuccess({ orgID, rule: res.data }));
		Notification('success', alertRuleID ? 'Global alert updated.' : 'Global alert created.');
	} catch (error) {
		Notification('error', 'Could not save the global alert.');
		yield put(globalAlertRulesActions.saveRuleError({ orgID }));
		yield put(globalAlertRulesActions.loadRules({ orgID }));
	}
}

// Fire-and-report: no list to reconcile on failure (unlike save/delete), just a toast.
function* testFire(action) {
	const orgID = yield select(selectOrgID);
	const { eventType, deliveryLevel, recipients } = action.payload;
	try {
		const res = yield call(() =>
			SuperFetch.post('/alerts/rules/test-fire', { type: 'global', eventType, deliveryLevel, recipients })
		);
		if (!okStatus(res)) throw new Error(res.message || 'Failed to send the test.');
		yield put(globalAlertRulesActions.testFireSuccess({ orgID }));
		Notification(
			res.data?.recipients ? 'success' : 'info',
			res.data?.recipients
				? `Test notification sent to ${res.data.recipients} recipient${res.data.recipients > 1 ? 's' : ''}.`
				: 'No recipients to notify with this configuration.'
		);
	} catch (error) {
		Notification('error', 'Could not send the test notification.');
		yield put(globalAlertRulesActions.testFireError({ orgID }));
	}
}

function* removeRule(action) {
	const orgID = yield select(selectOrgID);
	const { alertRuleID } = action.payload;
	try {
		const res = yield call(() => SuperFetch.delete(`/alerts/rules/${alertRuleID}`));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to delete the global alert.');
		yield put(globalAlertRulesActions.deleteRuleSuccess({ orgID, alertRuleID }));
		Notification('success', 'Global alert deleted.');
	} catch (error) {
		Notification('error', 'Could not delete the global alert.');
		yield put(globalAlertRulesActions.deleteRuleError({ orgID }));
		yield put(globalAlertRulesActions.loadRules({ orgID }));
	}
}

export default function* globalAlertRulesSaga() {
	yield all([
		takeLatest(globalAlertRulesActions.loadRules, fetchRules),
		takeEvery(globalAlertRulesActions.saveRule, saveRule),
		takeEvery(globalAlertRulesActions.deleteRule, removeRule),
		takeEvery(globalAlertRulesActions.testFire, testFire)
	]);
}
